import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import { getCookie, hasNonUsMarketingConsent } from 'Helpers/Cookie/CookieUtils';
import { currentBreakpoint } from 'Hooks/useViewport';
import store from 'Redux/store';


const ATTRIBUTION_STORAGE_KEY = 'landing_page';
const COOKIE_EXPIRY_DAYS = 30;
const MS_PER_DAY = 86400000; // 24 hours in milliseconds

const AttributionUtils = {};

// Lower number = higher priority. A new channel can only overwrite
// an existing (non-expired) attribution if its priority is lower than the stored one.
const CHANNEL_PRIORITY = {
  Awin: 1,
  Affiliate: 2,
  Paid: 3,
  CRM: 4,
  Assistant: 5,
  FandomMedia: 6,
  Fandom: 7,
  Partner: 8,
  Other: 9,
};

const OTHER_AFFILIATE_SOURCES = ['cj', 'admitad', 'allkeyshop', 'rakuten', 'airtime'];
const OTHER_AFFILIATE_PARAMS = ['aff_id', 'click_id', 'aff_track', 'aff_sub', 'cjevent', 'dc_pid', 'adm_pid', 'tap_click_id'];
const PAID_MEDIUMS = ['cpc', 'ppc', 'paid', 'paid-search', 'paid-social', 'social_paid', 'shopping', 'paid-media'];
const PAID_PARAMS = ['gclid', 'msclkid', 'gbraid', 'wbraid', 'fbclid', 'rdt_cid'];
const CRM_SOURCES = ['newsletter', 'transactional', 'wishlist_email', 'triggered', 'fanatical transactional email', 'fanatical newsletter', 'email_trigger', 'email_automation', 'fanatical cart', 'fanatical welcome', 'standard'];
const FANDOM_MEDIA_SOURCES = ['metacritic', 'gamespot', 'gamefaqs', 'tvguide', 'giantbomb'];

/**
 * Classify the current landing using the URL
 *
 * Returns:
 *   @String channelName - The name of the channel
 */
const detectChannel = () => {
  const utmSource = (getUrlParameter('utm_source') || '').toLowerCase();
  const utmMedium = (getUrlParameter('utm_medium') || '').toLowerCase();

  // Awin - primary Awin click parameter or explicit utm_source
  if (utmSource === 'awin' || !!getUrlParameter('awc')) {
    return 'Awin';
  }

  // Other Affiliate Network
  if (
    utmMedium === 'affiliate'
    || OTHER_AFFILIATE_SOURCES.includes(utmSource)
    || OTHER_AFFILIATE_PARAMS.some(param => !!getUrlParameter(param))
  ) {
    return 'Affiliate';
  }

  // Paid
  if (
    PAID_MEDIUMS.includes(utmMedium)
    || PAID_PARAMS.some(param => !!getUrlParameter(param))
  ) {
    return 'Paid';
  }

  // CRM (Email / Push)
  if (utmMedium === 'email' || utmMedium === 'push' || CRM_SOURCES.includes(utmSource)) {
    return 'CRM';
  }

  // Assistant (browser extensions)
  if (utmMedium === 'extension') {
    return 'Assistant';
  }

  // Fandom Media
  if (FANDOM_MEDIA_SOURCES.includes(utmSource)) {
    return 'FandomMedia';
  }

  // Fandom
  if (utmSource === 'fandom') {
    return 'Fandom';
  }

  // Partner
  if (utmSource === 'partner' || utmMedium === 'partner') {
    return 'Partner';
  }

  // Direct / Organic / Referral / Unknown
  return 'Other';
};


/**
 * Persist landing page attribution data in localStorage.
 * A new channel only overwrites an existing (non-expired) attribution
 * if its priority is higher or equal (lower number = higher priority).
 * @returns {void}
 */
AttributionUtils.saveAttribution = () => {
  const channel = detectChannel();

  if (channel === null) {
    return;
  }

  const newPriority = CHANNEL_PRIORITY[channel] ?? CHANNEL_PRIORITY.Other;

  const existing = JSON.parse(localStorage.getItem(ATTRIBUTION_STORAGE_KEY));

  if (existing) {
    const expired = existing.expires <= Date.now();

    if (!expired) {
      const existingPriority = CHANNEL_PRIORITY[existing.channel] ?? CHANNEL_PRIORITY.Other;

      if (newPriority > existingPriority) {
        // New channel has lower priority — do not overwrite
        return;
      }
    }
  }

  const now = Date.now();

  const nonUsMarketingConsent = hasNonUsMarketingConsent();

  const attribution = {
    url: window.location.href,
    date: now,
    channel,
    expires: now + COOKIE_EXPIRY_DAYS * MS_PER_DAY,
    utmSource: getUrlParameter('utm_source'),
    utmMedium: getUrlParameter('utm_medium'),
    nonUsMarketingConsent,
    otherFields: {
      awc: getUrlParameter('awc'),
      clickId: getUrlParameter('click_id'),
      campaignId: getUrlParameter('utm_campaign'),
      siteLanguage: window.locale,
      siteTheme: store.getState().siteTheme,
      browserLanguage: navigator.language,
      referrer: document.referrer,
      viewportBreakpoint: currentBreakpoint(window.innerWidth),
      userAgent: navigator.userAgent,
    },
  };

  if (nonUsMarketingConsent) {
    attribution.otherFields.rdtCid = getUrlParameter('rdt_cid');
    attribution.otherFields.rdtUuid = getCookie('_rdt_uuid');
  }

  localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
};

/**
 * Retrieve the current (non-expired) affiliate attribution from localStorage.
 *
 * @returns {object | null}
 */
AttributionUtils.getAttribution = () => {
  const attribution = JSON.parse(localStorage.getItem(ATTRIBUTION_STORAGE_KEY));

  if (!attribution || attribution.expires <= Date.now()) {
    return null;
  }

  // Always reflect the current consent state and tracking values, regardless of when attribution was stored
  const nonUsMarketingConsent = hasNonUsMarketingConsent();
  attribution.nonUsMarketingConsent = nonUsMarketingConsent;

  if (nonUsMarketingConsent) {
    attribution.otherFields.rdtCid = getUrlParameter('rdt_cid');
    attribution.otherFields.rdtUuid = getCookie('_rdt_uuid');
  } else {
    delete attribution.otherFields.rdtCid;
    delete attribution.otherFields.rdtUuid;
  }

  return attribution;
};

export default AttributionUtils;
